/**
 * Workspace · Petty Cash count history ledger (S39).
 *
 * Mirrors `prototype/cash/petty-cash-counts.html` 1:1: a payments-style ledger
 * (KPI strip + filter chips + table-card) but where each row is clickable
 * to reveal a 3-column inline detail — reconciliation summary, source +
 * posting preview (Dr/Cr for movement rows; an audit-only callout for
 * zero-variance rows), and an audit-trail strip with a deep-link into the
 * audit log + an optional "Reverse this count" link.
 *
 * Backed by `pettyCash.countsHistory` (movement + audit merged) and
 * `cashMovements.countsStats` for the KPI tiles. ADR-0012 §10.4 governs
 * the zero-variance shortcut (no `cash_movements` row written).
 *
 * @see prototype/cash/petty-cash-counts.html
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10.4
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  RotateCcw,
  Search,
  TrendingDown,
  TrendingUp,
  Check,
} from "lucide-react";
import { Alert, Card, CardBody } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { StatTile, type StatTileTone } from "../../../../features/master-list/index.ts";
import { OUTCOME_CHIP_CLASS } from "../../../../features/petty-cash/index.ts";
import { paiseToInr, formatDate } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/cash/petty-cash/counts")({
  component: CountsHistoryPage,
});

function CountsHistoryPage() {
  const anchorQ = trpc.pettyCash.anchorData.useQuery({});
  const drawerId = anchorQ.data?.drawer.id;

  const historyQ = trpc.pettyCash.countsHistory.useQuery(
    { accountId: drawerId ?? "", limit: 100 },
    { enabled: !!drawerId },
  );
  const statsQ = trpc.cashMovements.countsStats.useQuery(
    { accountId: drawerId ?? "" },
    { enabled: !!drawerId },
  );

  if (anchorQ.isLoading) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Count history" },
        ]}
      >
        <Card>
          <CardBody>
            <p className="text-sm text-muted">Loading counts…</p>
          </CardBody>
        </Card>
      </WorkspaceShell>
    );
  }
  if (anchorQ.error || !anchorQ.data) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Cash" },
          { label: "Petty cash", to: "/cash/petty-cash" },
          { label: "Count history" },
        ]}
      >
        <Alert
          intent="bad"
          title="Couldn't load drawer"
          description={anchorQ.error?.message ?? "Missing data"}
        />
      </WorkspaceShell>
    );
  }

  return (
    <CountsHistoryBody
      drawerName={anchorQ.data.drawer.name}
      rows={historyQ.data?.rows ?? []}
      stats={statsQ.data ?? null}
      loading={historyQ.isLoading}
      error={historyQ.error?.message ?? null}
    />
  );
}

interface CountsStatsWire {
  countsRun: number;
  expectedCounts: number;
  averageVariancePaise: string;
  longestCleanStreakMonths: number;
  biggestShortPaise: string;
  biggestOverPaise: string;
}

interface DenominationsWire {
  d500: number;
  d200: number;
  d100: number;
  d50: number;
  d20: number;
  d10: number;
  d5: number;
  d2: number;
  d1: number;
}

interface HistoryRow {
  id: string;
  countedAt: string;
  variancePaise: string;
  outcome: "exact" | "short" | "over";
  narration: string;
  source: "movement" | "audit";
  postedAt: string;
  postedByName: string | null;
  postedById: string | null;
  bookBalancePaise: string | null;
  countedPaise: string | null;
  denominations: DenominationsWire | null;
}

interface CountRow {
  id: string;
  countedAt: string;
  variancePaise: bigint;
  outcome: "exact" | "short" | "over";
  narration: string;
  source: "movement" | "audit";
  postedAt: string;
  postedByName: string | null;
  postedById: string | null;
  bookBalancePaise: bigint | null;
  countedPaise: bigint | null;
  denominations: DenominationsWire | null;
}

function CountsHistoryBody({
  drawerName,
  rows,
  stats,
  loading,
  error,
}: {
  drawerName: string;
  rows: ReadonlyArray<HistoryRow>;
  stats: CountsStatsWire | null;
  loading: boolean;
  error: string | null;
}) {
  const [query, setQuery] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState<"all" | "exact" | "short" | "over">("all");
  const [expanded, setExpanded] = useState<ReadonlyMap<string, true>>(new Map());

  // The history wire already classifies the outcome — convert paise to bigint
  // for the variance formatter; no sign re-derivation needed. Reconciliation
  // fields (book / counted / denominations / posting actor + at) come back
  // nullable so legacy rows (counts posted before the audit-metadata fix
  // landed) can still render — just with a "Legacy count" muted hint.
  const countRows: ReadonlyArray<CountRow> = useMemo(() => {
    return rows.map(
      (r): CountRow => ({
        id: r.id,
        countedAt: r.countedAt,
        variancePaise: BigInt(r.variancePaise),
        outcome: r.outcome,
        narration: r.narration,
        source: r.source,
        postedAt: r.postedAt,
        postedByName: r.postedByName,
        postedById: r.postedById,
        bookBalancePaise: r.bookBalancePaise === null ? null : BigInt(r.bookBalancePaise),
        countedPaise: r.countedPaise === null ? null : BigInt(r.countedPaise),
        denominations: r.denominations,
      }),
    );
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return countRows.filter((r) => {
      if (outcomeFilter !== "all" && r.outcome !== outcomeFilter) return false;
      if (!q) return true;
      return r.narration.toLowerCase().includes(q) || r.countedAt.includes(q);
    });
  }, [countRows, outcomeFilter, query]);

  // Pre-expand the first row on first load so the design reads at a glance.
  // Re-runs only when the filtered set changes — never reaches in and stomps
  // a user-collapsed row.
  useEffect(() => {
    if (filtered.length === 0) return;
    setExpanded((prev) => {
      if (prev.size > 0) return prev;
      const next = new Map<string, true>();
      next.set(filtered[0]!.id, true);
      return next;
    });
    // We intentionally depend only on the first row's id so re-filtering doesn't
    // collapse other rows the user has opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered[0]?.id]);

  const toggleRow = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, true);
      return next;
    });
  }, []);

  // Esc collapses all expanded rows (matches prototype). Guarded so typing
  // in the search input never collapses underneath the user.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      setExpanded(new Map());
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── KPI tiles ─ derived from server `cashMovements.countsStats`.
  //    Bigint paise come over the wire as signed integer strings.
  const avgAbsVariancePaise = stats === null ? 0n : absBig(BigInt(stats.averageVariancePaise));
  const biggestShortPaise = stats === null ? 0n : absBig(BigInt(stats.biggestShortPaise));
  const countsRun = stats?.countsRun ?? 0;
  const longestCleanStreakMonths = stats?.longestCleanStreakMonths ?? 0;

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Petty cash", to: "/cash/petty-cash" },
        { label: "Count history" },
      ]}
    >
      <div className="flex flex-col gap-5">
        {/* Page header */}
        <header className="flex flex-wrap items-start gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/cash/petty-cash"
              className="inline-grid place-items-center w-9 h-9 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg no-underline"
              aria-label="Back to petty cash"
            >
              <ArrowLeft size={16} strokeWidth={2} />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
                Count history
              </h1>
              <p className="mt-1 text-xs text-muted">
                {drawerName} · {countRows.length} {countRows.length === 1 ? "count" : "counts"} on
                record · click a row to expand
              </p>
            </div>
          </div>
        </header>

        {/* KPI strip */}
        <section
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
          aria-label="Count history summary"
        >
          <StatTile
            label="Counts on record"
            value={countRows.length.toString()}
            meta={
              countsRun === countRows.length
                ? "Variance-bearing only"
                : `${countsRun} variance · ${countRows.length - countsRun} clean (audit-derived)`
            }
            tone="sky"
            icon={Check}
          />
          <StatTile
            label="Average variance"
            value={avgAbsVariancePaise === 0n ? "₹0" : `±${paiseToInr(avgAbsVariancePaise)}`}
            meta={
              countsRun === 0
                ? "No counts yet"
                : `Across ${countsRun} variance-bearing ${countsRun === 1 ? "count" : "counts"}`
            }
            tone={varianceTone(avgAbsVariancePaise)}
            icon={TrendingDown}
          />
          <StatTile
            label="Longest clean streak"
            value={`${longestCleanStreakMonths} mo`}
            meta="Consecutive months without a variance-bearing count"
            tone="muted"
            icon={TrendingUp}
          />
          <StatTile
            label="Biggest short"
            value={biggestShortPaise === 0n ? "—" : `−${paiseToInr(biggestShortPaise)}`}
            meta={biggestShortPaise === 0n ? "No shorts on record" : "Single largest short YTD"}
            tone={biggestShortPaise === 0n ? "muted" : "warn"}
            icon={TrendingDown}
          />
        </section>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2">
          <OutcomePill active={outcomeFilter === "all"} onClick={() => setOutcomeFilter("all")}>
            All
          </OutcomePill>
          <OutcomePill active={outcomeFilter === "exact"} onClick={() => setOutcomeFilter("exact")}>
            Exact
          </OutcomePill>
          <OutcomePill active={outcomeFilter === "short"} onClick={() => setOutcomeFilter("short")}>
            Short
          </OutcomePill>
          <OutcomePill active={outcomeFilter === "over"} onClick={() => setOutcomeFilter("over")}>
            Over
          </OutcomePill>
          <label className="inline-flex items-center gap-1.5 bg-paper border border-line rounded-sm px-2 py-1 flex-1 min-w-[200px] max-w-[320px] focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100">
            <Search size={12} className="text-muted" strokeWidth={2} aria-hidden />
            <input
              type="search"
              placeholder="Search by date or narration…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search counts"
              className="flex-1 bg-transparent border-0 outline-none text-xs text-ink placeholder:text-muted"
            />
          </label>
        </div>

        {/* Table */}
        {loading ? (
          <Card>
            <CardBody>
              <p className="text-sm text-muted">Loading count rows…</p>
            </CardBody>
          </Card>
        ) : error ? (
          <Alert intent="bad" title="Couldn't load count history" description={error} />
        ) : filtered.length === 0 ? (
          <Card>
            <CardBody>
              <p className="text-sm text-muted">
                {countRows.length === 0
                  ? "No physical counts on record yet. Run one to seed the audit trail."
                  : "No counts match these filters."}
              </p>
            </CardBody>
          </Card>
        ) : (
          <section className="bg-paper border border-line rounded-md shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-line">
              <span className="text-sm font-semibold text-ink">
                {filtered.length} {filtered.length === 1 ? "count" : "counts"} · newest first
              </span>
              <span className="text-xs text-muted">
                Click any row for reconciliation + posting preview · press Esc to collapse
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-bg">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-line">
                  <th className="w-8 px-2 py-2.5" aria-label="Expand"></th>
                  <th className="px-4 py-2.5 font-semibold">Counted on</th>
                  <th className="px-4 py-2.5 font-semibold">Outcome</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Variance</th>
                  <th className="px-4 py-2.5 font-semibold">Narration</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Voucher</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isOpen = expanded.has(r.id);
                  return (
                    <CountRowPair
                      key={r.id}
                      row={r}
                      open={isOpen}
                      onToggle={() => toggleRow(r.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </WorkspaceShell>
  );
}

// ── Row + expanded panel ───────────────────────────────────────────

function CountRowPair({
  row,
  open,
  onToggle,
}: {
  row: CountRow;
  open: boolean;
  onToggle: () => void;
}) {
  const voucher = voucherLabel(row);
  const internalId = row.id.replace(/-/g, "").slice(0, 8);
  const isAudit = row.source === "audit";
  const absVariance = absBig(row.variancePaise);
  const varianceLabel =
    row.variancePaise === 0n
      ? "₹0"
      : `${row.variancePaise > 0n ? "+" : "−"}${paiseToInr(absVariance)}`;

  const varTextClass =
    row.outcome === "exact" ? "text-ok" : row.outcome === "over" ? "text-sky-700" : "text-warn";

  return (
    <>
      <tr
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={`detail-${row.id}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className={
          "cursor-pointer border-b border-line last:border-b-0 transition-colors " +
          (open ? "bg-raised" : "hover:bg-bg")
        }
      >
        <td className="w-8 px-2 py-3 align-middle">
          <span
            aria-hidden
            className={
              "inline-grid place-items-center w-6 h-6 rounded-sm transition-all " +
              (open
                ? "rotate-90 text-sky-700 bg-sky-50"
                : "text-muted hover:bg-paper hover:text-ink")
            }
          >
            <ChevronRight size={12} strokeWidth={2.4} />
          </span>
        </td>
        <td className="px-4 py-3 align-middle num font-medium">{formatDate(row.countedAt)}</td>
        <td className="px-4 py-3 align-middle">
          <span
            className={
              "inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold capitalize " +
              OUTCOME_CHIP_CLASS[row.outcome]
            }
          >
            {row.outcome}
          </span>
        </td>
        <td
          className={
            "px-4 py-3 align-middle text-right num tabular-nums font-semibold " + varTextClass
          }
        >
          {varianceLabel}
        </td>
        <td className="px-4 py-3 align-middle text-xs truncate max-w-[360px]">{row.narration}</td>
        <td className="px-4 py-3 align-middle text-right">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-bg border border-line num tabular-nums text-xs font-semibold text-ink-2">
            {voucher}
          </span>
        </td>
      </tr>
      {open ? (
        <tr id={`detail-${row.id}`} className="border-b border-line last:border-b-0">
          <td colSpan={6} className="p-0 bg-raised">
            <div className="pl-12 pr-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-7">
              {/* Left · Reconciliation + denomination grid */}
              <DetailSection label="Reconciliation">
                {row.bookBalancePaise === null && row.countedPaise === null ? (
                  <div className="mb-2 text-xs text-muted leading-relaxed italic">
                    Legacy count — book balance &amp; denominations not recorded.
                  </div>
                ) : (
                  <>
                    <ReconLine
                      k="Book balance"
                      v={row.bookBalancePaise === null ? "—" : paiseToInr(row.bookBalancePaise)}
                    />
                    <ReconLine
                      k="Counted"
                      v={row.countedPaise === null ? "—" : paiseToInr(row.countedPaise)}
                    />
                  </>
                )}
                <ReconLine k="Variance" v={varianceLabel} tone={varTone(row.outcome)} />
                <div className="mt-2">
                  <p className="text-xs text-muted mb-1">|variance| vs ₹500 reference</p>
                  <div className="relative h-[5px] bg-line rounded-full overflow-hidden">
                    <i
                      aria-hidden
                      className={
                        "absolute top-0 bottom-0 left-0 rounded-full " +
                        (row.outcome === "exact"
                          ? "bg-ok"
                          : row.outcome === "short"
                            ? "bg-warn"
                            : "bg-sky-600")
                      }
                      style={{
                        width:
                          row.outcome === "exact"
                            ? "2%"
                            : `${Math.min(Math.max(Number(absVariance / 100n) / 5, 4), 100)}%`,
                      }}
                    />
                  </div>
                </div>
                {row.denominations ? (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
                      Denomination breakdown
                    </p>
                    <DenominationGrid denominations={row.denominations} />
                  </div>
                ) : null}
              </DetailSection>

              {/* Middle · Source + posting preview */}
              <DetailSection label={isAudit ? "Source" : "Source · Posting preview"}>
                <SourcePill source={row.source} />
                {isAudit ? (
                  <div className="mt-2.5 rounded-sm border border-sky-100 bg-sky-50 px-3 py-2.5 text-xs text-sky-700 leading-relaxed">
                    <strong className="text-ink font-semibold">Zero-variance count</strong>
                    {" — "}
                    <code className="font-mono text-xs">bank_accounts.last_counted_at</code>
                    {" bumped only. No "}
                    <code className="font-mono text-xs">cash_movements</code>
                    {" row written. "}
                    <a
                      href="https://github.com/flowtrail/flint/blob/main/docs/adr/0012-bank-accounts-and-cash-movements.md"
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-sky-700 hover:underline"
                    >
                      ADR-0012 §10.4
                    </a>
                  </div>
                ) : (
                  <PostingPreview outcome={row.outcome} amountPaise={absVariance} />
                )}
              </DetailSection>

              {/* Right · Audit trail */}
              <DetailSection label="Audit trail">
                <MetaLine k="Voucher" v={voucher} />
                <MetaLine k="Internal ID" v={internalId} />
                <MetaLine k="Posted at" v={formatDateTime(row.postedAt)} title={row.postedAt} />
                <MetaLine
                  k="Posted by"
                  v={
                    row.postedByName ??
                    (row.postedById ? row.postedById.replace(/-/g, "").slice(0, 8) : "—")
                  }
                  title={row.postedById ?? undefined}
                />
                <MetaLine k="Source" v={isAudit ? "audit_entries.counted" : "cash_movements"} />
                <div className="mt-3 pt-2.5 border-t border-dashed border-line flex flex-col gap-1.5 text-xs">
                  <Link
                    to="/records/audit-log"
                    className="inline-flex items-center gap-1.5 text-sky-700 hover:underline font-medium"
                  >
                    <ArrowRight size={12} strokeWidth={2} aria-hidden />
                    Open in audit log
                  </Link>
                  {!isAudit ? (
                    <Link
                      to="/cash/petty-cash/$voucherId"
                      params={{ voucherId: row.id }}
                      className="inline-flex items-center gap-1.5 text-bad hover:underline font-medium"
                      title="Voiding posts an append-only reversal row per ADR-0012 §3"
                    >
                      <RotateCcw size={12} strokeWidth={2} aria-hidden />
                      Reverse this count
                    </Link>
                  ) : null}
                </div>
              </DetailSection>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ── Inline sub-components (small enough to co-locate per spec) ─────

function DetailSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <h4 className="text-xs font-bold uppercase tracking-wider text-muted mb-2.5">{label}</h4>
      {children}
    </div>
  );
}

function ReconLine({
  k,
  v,
  tone,
}: {
  k: string;
  v: string;
  tone?: "ok" | "warn" | "over" | "ink";
}) {
  const toneClass =
    tone === "ok"
      ? "text-ok"
      : tone === "warn"
        ? "text-warn"
        : tone === "over"
          ? "text-sky-700"
          : "text-ink";
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-xs border-t border-dashed border-line first:border-t-0">
      <span className="text-muted">{k}</span>
      <span className={"font-mono font-semibold tracking-tight " + toneClass}>{v}</span>
    </div>
  );
}

function MetaLine({ k, v, title }: { k: string; v: string; title?: string | undefined }) {
  return (
    <div className="flex items-baseline gap-2.5 py-0.5 text-xs">
      <span className="text-muted min-w-[68px] text-xs">{k}</span>
      <span className="font-mono text-ink-2 text-xs" title={title}>
        {v}
      </span>
    </div>
  );
}

/**
 * Renders the 9-row denomination breakdown that backs a count voucher.
 * One row per RBI denomination, with `pieces × ₹N = subtotal`. Skips rows
 * with zero pieces so a partial count (banknotes-only / coins-only) reads
 * cleanly instead of a wall of `0 × ₹X = ₹0` lines.
 */
function DenominationGrid({ denominations }: { denominations: DenominationsWire }) {
  const ROWS: ReadonlyArray<{
    key: keyof DenominationsWire;
    label: string;
    valuePaise: bigint;
  }> = [
    { key: "d500", label: "₹500", valuePaise: 50000n },
    { key: "d200", label: "₹200", valuePaise: 20000n },
    { key: "d100", label: "₹100", valuePaise: 10000n },
    { key: "d50", label: "₹50", valuePaise: 5000n },
    { key: "d20", label: "₹20", valuePaise: 2000n },
    { key: "d10", label: "₹10", valuePaise: 1000n },
    { key: "d5", label: "₹5", valuePaise: 500n },
    { key: "d2", label: "₹2", valuePaise: 200n },
    { key: "d1", label: "₹1", valuePaise: 100n },
  ];

  const populated = ROWS.filter((r) => denominations[r.key] > 0);
  if (populated.length === 0) {
    return (
      <p className="text-xs text-muted italic">No pieces recorded against any denomination.</p>
    );
  }

  let grandPaise = 0n;
  for (const r of populated) {
    grandPaise += BigInt(denominations[r.key]) * r.valuePaise;
  }

  return (
    <table className="w-full text-xs font-mono">
      <tbody>
        {populated.map((r) => {
          const pieces = denominations[r.key];
          const subtotal = BigInt(pieces) * r.valuePaise;
          return (
            <tr key={r.key} className="border-b border-dashed border-line last:border-b-0">
              <td className="py-1 text-muted">{r.label}</td>
              <td className="py-1 text-right tabular-nums text-ink-2">× {pieces}</td>
              <td className="py-1 text-right tabular-nums text-ink font-semibold">
                {paiseToInr(subtotal)}
              </td>
            </tr>
          );
        })}
        <tr>
          <td className="pt-1.5 text-xs uppercase tracking-wider text-muted font-semibold">
            Total
          </td>
          <td className="pt-1.5"></td>
          <td className="pt-1.5 text-right tabular-nums text-ink font-bold">
            {paiseToInr(grandPaise)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * Locale-aware datetime renderer for the "Posted at" line. Falls back to
 * the raw ISO when `Date` can't parse the input (which would only happen
 * if the wire shape regresses).
 */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SourcePill({ source }: { source: "movement" | "audit" }) {
  if (source === "audit") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-50 border border-sky-100 text-xs font-bold uppercase tracking-wider text-sky-700">
        Audit
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-bg border border-line text-xs font-bold uppercase tracking-wider text-ink-2">
      Movement
    </span>
  );
}

/**
 * Dr/Cr posting preview block. For short, drawer is credited (decreased)
 * against a "Cash shortage" expense. For over, drawer is debited
 * (increased) against a "Cash overage" income.
 */
function PostingPreview({
  outcome,
  amountPaise,
}: {
  outcome: "exact" | "short" | "over";
  amountPaise: bigint;
}) {
  if (outcome === "exact") return null;
  const amount = paiseToInr(amountPaise);
  const isOver = outcome === "over";
  return (
    <div className="mt-2.5 rounded-sm border border-line bg-paper px-3 py-2.5 font-mono text-xs text-ink-2 leading-relaxed">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1.5">
        cash_movements.kind · cash_count_adjustment
      </p>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-ok font-semibold">
          Dr · {isOver ? "Office Drawer" : "Cash shortage"}
        </span>
        <span className="font-semibold">{amount}</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-bad font-semibold">
          Cr · {isOver ? "Cash overage" : "Office Drawer"}
        </span>
        <span className="font-semibold">{amount}</span>
      </div>
    </div>
  );
}

function OutcomePill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center h-7 px-2.5 rounded-sm border text-xs font-semibold transition-colors " +
        (active
          ? "bg-sky-50 border-sky-200 text-sky-700"
          : "bg-paper border-line text-ink-2 hover:bg-raised")
      }
    >
      {children}
    </button>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function absBig(n: bigint): bigint {
  return n < 0n ? -n : n;
}

function varianceTone(paise: bigint): StatTileTone {
  if (paise === 0n) return "ok";
  if (paise < 5000n) return "sky";
  if (paise < 20000n) return "warn";
  return "bad";
}

function varTone(outcome: "exact" | "short" | "over"): "ok" | "warn" | "over" | "ink" {
  if (outcome === "exact") return "ok";
  if (outcome === "short") return "warn";
  return "over";
}

/**
 * Derive a printable voucher pill from a count row. We don't get the
 * actual `voucher_no` over the wire — the api-contract surfaces just
 * `(id, countedAt, …)` — so we synthesise a `PCV-COUNT-YYYYMMDD-xxxx`
 * label using the count date and the first 4 hex chars of the UUID.
 * Matches the prototype's mock voucher numbering.
 */
function voucherLabel(row: CountRow): string {
  const ymd = row.countedAt.replace(/-/g, "").slice(0, 8);
  const shortId = row.id.replace(/-/g, "").slice(0, 4);
  return `PCV-COUNT-${ymd}-${shortId}`;
}
